import React, {useEffect, useMemo, useState} from "react";
import {useMap} from "@vis.gl/react-google-maps";
import {ClusteredMarker} from "@/pages/workspaces/sales/ClusteredMarker";
import Supercluster from 'supercluster';

/**
 * Transforms a GeoJSON Feature from Supercluster into a standardized marker object.
 * Handles both individual points and aggregate clusters.
 */
const toSuperclusterPoint = (feature) => {
  const coords = feature?.geometry?.coordinates;

  // GeoJSON Point coordinates are [lng, lat]
  const lng = Array.isArray(coords) ? Number(coords[0]) : NaN;
  const lat = Array.isArray(coords) ? Number(coords[1]) : NaN;

  const cluster = feature?.properties?.cluster === true;

  // Supercluster clusters have point_count; non-clusters are single points
  const point_count = cluster
    ? Number(feature?.properties?.point_count ?? 0)
    : 1;

  let id
  if (cluster) id = `cluster-${feature?.properties?.cluster_id}`
  else id = feature?.properties?.id

  let name
  if (cluster) name = `${point_count} sites`
  else name = feature?.properties?.name

  const isActive = feature?.properties?.isActive

  return {id, lat, lng, cluster, point_count, name, isActive, properties: feature?.properties};
};

/**
 * Validates that a marker has a valid ID and geographic coordinates.
 */
const isValidMarker = ({id, lat, lng}) =>
  Boolean(id) &&
  Number.isFinite(lat) &&
  Number.isFinite(lng) &&
  lat >= -90 && lat <= 90 &&
  lng >= -180 && lng <= 180;


/**
 * Component responsible for rendering a layer of markers that cluster together
 * based on zoom level and proximity using the Supercluster library.
 */
export const ClusteredMarkerLayer = ({visibleSites}) => {
  const [superMarkers, setSuperMarkers] = useState({});

  // Get the Google Maps instance from context
  const map = useMap();
  const zoom = map.getZoom()

  // Initialize the Supercluster indexer
  const supercluster = useMemo(() => {
    if (!map) return null;

    return new Supercluster({maxZoom: 12, minPoints: 15});
  }, [map]);

  // Load sites into the clusterer and calculate clusters whenever visibleSites or the clusterer changes
  useEffect(() => {
    if (!supercluster) return;

    // Convert flat site data into GeoJSON format for Supercluster
    supercluster.load(visibleSites.map(site => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [site.longitude, site.latitude]
      },
      properties: {
        ...site
      }
    })));

    // Retrieve clusters for the entire world view at the current zoom level. We expect only visible sites, so we do not
    // use a real bounding box.
    const mapped = supercluster.getClusters([-180, -90, 180, 90], zoom)
      .map(toSuperclusterPoint)
      .filter(isValidMarker);
    setSuperMarkers(mapped)
  }, [supercluster, visibleSites]);

  if (!superMarkers || !superMarkers.length) return null;
  return (
    <>
      {superMarkers.map((site) => (
        <ClusteredMarker key={site.id} site={site}/>
      ))
      }
    </>
  )
}

